import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, inject, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventType, Router, Scroll } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { distinctUntilChanged, EMPTY, filter, first, map, Observable, switchMap, tap } from 'rxjs';
import { ScreenSize, screenSizes, TW_BREAKPOINTS } from '../common/tw-theme';
import { SEOService } from '../services/seo';
import { isServer } from '../utilities/platform-type';
import { uiActions } from './ui';

export const uiEffects = {
  validateScreenDeclarations: createEffect(
    () => {
      if (isServer() || !isDevMode())
        return EMPTY;

      const screens = inject(TW_BREAKPOINTS);
      const themeSizes = Object.keys(screens);

      validateScreenDeclarations(themeSizes, screenSizes);

      return EMPTY;
    },
    { functional: true, dispatch: false }),
  updateBreakpoints: createEffect(
    () => {
      if (isServer())
        return EMPTY;

      const screens = inject(TW_BREAKPOINTS);

      const breakpoints = new Map(
        screenSizes.map(size => [size, `(min-width: ${screens[size]})`])
      );

      const bo = inject(BreakpointObserver);
      return bo.observe([...breakpoints.values()])
        .pipe(
          map(observed => {
            let result: ScreenSize = 'xs';
            for (const size of screenSizes) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const bp = breakpoints.get(size)!;
              if (observed.breakpoints[bp] === true)
                result = size;
              else
                break;
            }
            return result;
          }),
          distinctUntilChanged(),
          map(size => uiActions.updateScreenSize({ payload: { size } }))
        );
    },
    { functional: true }),
  watchRouterNavigation: createEffect(
    () => {
      const router = inject(Router);

      return router.events.pipe(
        // TODO: implement auth.ready$
        // switchMap(event => auth.ready$.then(() => event instanceof Scroll ? event.routerEvent : event)),
        // TODO: remove the following line after auth.ready$ is implemented
        map(event => event instanceof Scroll ? event.routerEvent : event),
        map(event => {
          switch (event.type) {
            case EventType.NavigationStart:
            case EventType.ResolveStart: return true;
            case EventType.NavigationEnd:
            case EventType.NavigationCancel:
            case EventType.NavigationError: return false;
            default: return undefined;
          }
        }),
        filter((nav): nav is boolean => typeof nav === 'boolean'),
        distinctUntilChanged(),
        map(navigating => uiActions.updateNavigationState({ navigating }))
      );
    },
    { functional: true }
  ),
  watchRouterSEO: createEffect(
    () => {
      const router = inject(Router);
      const seoService = inject(SEOService);

      return router.events
        .pipe(
          filter(e => e.type === EventType.ActivationEnd),
          tap(e => {
            const snap = e.snapshot;
            const seoData = snap.data?.['seo'];

            seoService.setTitle(seoData?.title);
            seoService.setDescription(seoData?.description);
          })
        );
    },
    { functional: true, dispatch: false }
  ),
  hideSplashScreen: createEffect(
    () => {
      const router = inject(Router);
      if (isServer())
        return EMPTY;

      return router.events
        .pipe(
          filter(e => e.type === EventType.NavigationEnd),
          first(),
          map(() => uiActions.hideSplashScreen())
        );
    },
    { functional: true }
  ),
  watchRouterState: createEffect(
    () => {
      const router = inject(Router);

      return router.events
        .pipe(
          filter(e => e.type === EventType.ActivationStart),
          switchMap(e => {
            const snap = e.snapshot;
            return [
              uiActions.updateMinimalUi({ payload: { minimal: snap.data['minimalUi'] || false } }),
              uiActions.updateRouteClass({ payload: { class: snap.data['routeClass'] || null } })
            ];
          })
        );
    },
    { functional: true }
  ),
  // TODO: check if this effect is still needed
  // revant issue: https://github.com/angular/components/issues/26451
  wrapLibraryStylesInLayer: createEffect(
    () => {
      // skip this effect during ssr
      if (isServer())
        return EMPTY;

      const document = inject(DOCUMENT);

      const shouldProcess = (() => {
        const prefixes = [
          '.mat-',
          '.mdc-',
          '.cdk-',
          'div.mat-', // autocomplete
          '@keyframes _mat-', // snackbar
        ];
        return (styleElement: HTMLStyleElement) => {
          const styleContent = styleElement.textContent || '';

          if (!styleContent)
            return false;

          if (styleElement.id === 'fa-auto-css')
            return true;

          return prefixes.some(p => styleContent.startsWith(p));
        };
      })();

      function wrapContents(styleElement: HTMLStyleElement) {
        const styleContent = styleElement.textContent || '';

        if (!shouldProcess(styleElement))
          return;

        styleElement.textContent = `@layer base { ${styleContent} }`;
      }

      const mutations$ = new Observable<Node>(subscriber => {
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => subscriber.next(node));
          });
        });

        document.head.childNodes.forEach(node => subscriber.next(node));

        observer.observe(document.head, { childList: true });

        return () => observer.disconnect();
      });

      return mutations$
        .pipe(
          takeUntilDestroyed(),
          filter(node => node instanceof HTMLStyleElement),
          tap(node => wrapContents(node))
        );
    },
    { functional: true, dispatch: false }
  )
} as const;

function validateScreenDeclarations(
  tailwind: readonly string[],
  screens: readonly string[]
) {

  const sets = {
    screens: new Set(screens),
    tailwind: new Set(tailwind),
  };

  for (const size of tailwind) {
    if (!sets.screens.has(size)) {
      throw new Error(`'${size}' is declared in tailwind config, but not declared in typescript screens`);
    }
  }

  for (const size of screens) {
    if (!sets.tailwind.has(size)) {
      throw new Error(`'${size}' is declared in typescript screen, but not declared in tailwind config`);
    }
  }
}